import React, { useEffect, useMemo, useState, useRef } from "react";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "./Tirth.module.scss";

import { getTirthAll, getTirthStates } from "../../services/apis/tirth";

import {
  FilterSection,
  ModernSlider,
  SearchCountContainer,
} from "../../library/component";
import {
  Pagination,
  BlogCard,
  Loader,
  StickySearchBar,
} from "../../library/common";

import localesKeys from "../../locales/localesKeys.json";
import NoResult from "../../library/common/NoResult/NoResult";

const ITEMS_PER_PAGE = 21;

const Tirth = () => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef();

  const initTirth = {
    list: [],
    total: 0,
    loader: true,
  };

  const [tirth, setTirth] = useState(initTirth);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [filters, setFilters] = useState({
    states: [],
    districts: [],
    expandes: ["states", "districts"],
    search: "",
  });

  const handleGetTirth = (appliedFilters = filters, page = currentPage) => {
    getTirthAll({ filters: appliedFilters, page, limit: ITEMS_PER_PAGE })
      .then((res) => {
        if (res?.success) {
          setTirth({
            list: res?.data?.list || [],
            total: res?.data?.total || 0,
            loader: false,
          });
        } else {
          setTirth({ ...initTirth, loader: false });
        }
      })
      .catch(() => {
        setTirth({ ...initTirth, loader: false });
      });
  };

  const handleFilterConfig = async (key, value, filtersObj, page = 1) => {
    let newFilters = {
      ...filtersObj,
      [key]: filtersObj?.[key]?.includes(value)
        ? filtersObj?.[key]?.filter((item) => item !== value)
        : [...filtersObj[key], value],
    };

    if (key === "states") {
      if (newFilters.states.length >= 1) {
        setDistricts([
          ...states
            .filter((curr) => newFilters.states.includes(curr.value))
            .flatMap((curr) => curr.districts),
        ]);
        // .flatMap((curr) => curr.districts);
      } else {
        setDistricts([]);
      }
      newFilters.districts = [];
    }

    setFilters(newFilters);
    handleGetTirth(newFilters, page);
  };

  useEffect(() => {
    getTirthStates()
      .then((res) => {
        if (res?.success) setStates(res?.data || []);
      })
      .catch(() => setStates([]));

    setDistricts([]);

    handleGetTirth(filters, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterConfig = useMemo(() => {
    return [
      {
        label: t(localesKeys.TIRTH_STATES_STATE),
        options: states,
        name: "states",
        onExpand: () => {
          setFilters({
            ...filters,
            expandes: filters?.expandes?.includes("states")
              ? filters?.expandes?.filter((item) => item !== "states")
              : [...filters.expandes, "states"],
          });
        },
        onCheckBoxMarked: (value) =>
          handleFilterConfig("states", value, filters, 1),
      },
      {
        label: t(localesKeys.TIRTH_DISTRICTS_DISTRICT),

        options: districts,
        name: "districts",
        onExpand: () => {
          setFilters({
            ...filters,
            expandes: filters?.expandes?.includes("districts")
              ? filters?.expandes?.filter((item) => item !== "districts")
              : [...filters.expandes, "districts"],
          });
        },
        onCheckBoxMarked: (value) =>
          handleFilterConfig("districts", value, filters, currentPage),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, districts, filters, t]);

  let startPage = Math.min(
    (currentPage - 1) * ITEMS_PER_PAGE + 1,
    tirth?.total
  );
  let endPage = Math.min(currentPage * ITEMS_PER_PAGE, tirth?.total);

  return (
    <div ref={scrollContainerRef} className={styles.tirthContainer}>
      <ModernSlider path="tirth" />

      <StickySearchBar
        className={"desktopSearchBar"}
        placeholder={t(localesKeys.SEARCH)}
        onSearch={(value) => {
          let newFilters = { ...filters, search: value };
          setFilters(newFilters);
          setCurrentPage(1);
          handleGetTirth(newFilters, 1);
        }}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
      />

      <div className={styles.containerFluid}>
        <Container className={styles.container}>
          <div className={`${styles.booksDisplayContainer}`}>
            <FilterSection
              config={filterConfig}
              setFilters={setFilters}
              currentPage={currentPage}
              handleGetBooks={handleGetTirth}
              filters={filters}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
            />

            <div className={styles.tirthListContainer}>
              <SearchCountContainer
                startPage={startPage}
                endPage={endPage}
                total={tirth?.total}
                onSearch={(value) => {
                  let newFilters = { ...filters, search: value };
                  setFilters(newFilters);
                  setCurrentPage(1);
                  handleGetTirth(newFilters, 1);
                }}
              />

              {tirth?.total ? (
                <div className={styles.gridContainer}>
                  {tirth?.list?.map((item, index) => (
                    <BlogCard key={index} {...item} path="tirth" />
                  ))}
                </div>
              ) : (
                <NoResult />
              )}

              <Pagination
                total={tirth?.total ? tirth?.total : 0}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                setCurrentPage={(n) => {
                  setCurrentPage(n);
                  handleGetTirth(filters, n);
                }}
                className={styles.paginationContainer}
              />
            </div>
          </div>
          {tirth?.loader && <Loader />}
        </Container>
      </div>
    </div>
  );
};

export default Tirth;
